import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import {
  Mail,
  Phone,
  Linkedin,
  Globe,
  Download,
  Share2,
  Copy,
  ArrowLeft,
  UserRound,
  Loader2,
} from "lucide-react";
import SEO from "../components/site/SEO";
import { LogoLockup } from "../components/site/Logo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Card() {
  const { slug } = useParams();
  const [director, setDirector] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/directors/by-slug/${slug}`);
        setDirector(data);
        document.title = `${data.name} — ${data.role} · Quasect`;
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050507] grid place-items-center text-[#8B93A5]">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }
  if (error || !director) {
    return (
      <div className="min-h-screen bg-[#050507] grid place-items-center text-center px-6">
        <div>
          <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#00F0FF]">
            CARD NOT FOUND
          </div>
          <h1 className="font-display font-black text-4xl mt-3">
            This card doesn't exist.
          </h1>
          <Link
            to="/"
            className="mt-7 inline-flex items-center gap-2 border border-white/15 hover:border-white px-5 py-3 text-sm uppercase tracking-wider"
          >
            <ArrowLeft className="w-4 h-4" /> Back to site
          </Link>
        </div>
      </div>
    );
  }

  const cardUrl = `${window.location.origin}/card/${slug}`;
  const vcfUrl = `${API}/cards/${slug}.vcf`;
  const email = director.email || "info@quasect.com";
  const phone = director.phone || "";
  const linkedin = director.linkedin || "";

  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${director.name} — Quasect`,
          text: `${director.name}, ${director.role} at Quasect`,
          url: cardUrl,
        });
      } catch {}
    } else {
      try {
        await navigator.clipboard.writeText(cardUrl);
        toast.success("Card link copied to clipboard");
      } catch {
        toast.error("Couldn't share card");
      }
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(cardUrl);
      toast.success("Link copied");
    } catch {
      toast.error("Couldn't copy");
    }
  };

  return (
    <div data-testid="card-page" className="min-h-screen bg-[#050507] text-white relative overflow-hidden">
      <SEO
        title={`${director.name} — ${director.role} · Quasect`}
        description={`Connect with ${director.name}, ${director.role} at Quasect. Save contact, email, or call directly.`}
        path={`/card/${slug}`}
      />
      <div className="absolute inset-0 bg-grid bg-grid-fade opacity-40 pointer-events-none" />

      {/* Top bar */}
      <header className="relative max-w-[1100px] mx-auto px-6 md:px-10 pt-6 md:pt-10 flex items-center justify-between">
        <Link to="/" data-testid="card-back" className="flex items-center gap-2 text-[#8B93A5] hover:text-white transition-colors text-sm">
          <ArrowLeft className="w-4 h-4" /> Back
        </Link>
        <Link to="/">
          <LogoLockup variant="dark" size={28} />
        </Link>
      </header>

      <main className="relative max-w-[1100px] mx-auto px-6 md:px-10 py-10 md:py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="grid lg:grid-cols-12 gap-6 lg:gap-8"
        >
          {/* LEFT — portrait + identity */}
          <div className="lg:col-span-7 bg-[#0A0A0D] border border-white/10 noise relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#39FF14] to-transparent" />
            <div className="grid sm:grid-cols-2 gap-0">
              <div className="aspect-square sm:aspect-auto bg-black relative">
                {director.image_data_url ? (
                  <img
                    src={director.image_data_url}
                    alt={`${director.name} portrait`}
                    className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-700"
                  />
                ) : (
                  <div className="w-full h-full grid place-items-center text-[#2a2d36]">
                    <UserRound className="w-24 h-24" />
                  </div>
                )}
                <div className="absolute top-4 left-4 font-mono text-[10px] tracking-[0.25em] uppercase bg-black/60 px-2 py-1 text-[#00F0FF]">
                  QUASECT // ID
                </div>
              </div>
              <div className="p-7 md:p-8 flex flex-col">
                <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#00F0FF]">
                  {director.role}
                </div>
                <h1
                  data-testid="card-name"
                  className="font-display font-black text-3xl md:text-4xl mt-2 tracking-tight leading-[1.05]"
                >
                  {director.name}
                </h1>
                <div className="font-mono text-[11px] tracking-[0.2em] uppercase text-[#39FF14] mt-3 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-[#39FF14] pulse-dot" /> ONLINE
                </div>
                <p className="text-[#8B93A5] text-sm mt-5 leading-relaxed line-clamp-6">
                  {director.bio}
                </p>
                <div className="mt-auto pt-6 font-mono text-[10px] tracking-widest text-[#8B93A5] uppercase">
                  QUASECT.COM
                </div>
              </div>
            </div>

            {/* Quick actions */}
            <div className="border-t border-white/10 grid grid-cols-2 md:grid-cols-4 divide-x divide-white/10">
              <Action
                href={`mailto:${email}`}
                icon={Mail}
                label="Email"
                value={email}
                testid="card-action-email"
              />
              {phone ? (
                <Action
                  href={`tel:${phone.replace(/\s/g, "")}`}
                  icon={Phone}
                  label="Call"
                  value={phone}
                  testid="card-action-phone"
                />
              ) : (
                <Action disabled icon={Phone} label="Call" value="—" />
              )}
              {linkedin ? (
                <Action
                  href={linkedin}
                  external
                  icon={Linkedin}
                  label="LinkedIn"
                  value="Profile"
                  testid="card-action-linkedin"
                />
              ) : (
                <Action disabled icon={Linkedin} label="LinkedIn" value="—" />
              )}
              <Action
                href="https://quasect.com"
                external
                icon={Globe}
                label="Website"
                value="quasect.com"
                testid="card-action-web"
              />
            </div>
          </div>

          {/* RIGHT — QR + save/share */}
          <div className="lg:col-span-5 space-y-6">
            <div className="bg-[#0A0A0D] border border-white/10 p-7 noise relative">
              <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#00F0FF]">
                Scan to save
              </div>
              <h2 className="font-display font-bold text-xl mt-2">Share this card</h2>
              <div className="mt-5 bg-white p-4 inline-block">
                <QRCodeSVG
                  value={cardUrl}
                  size={180}
                  bgColor="#FFFFFF"
                  fgColor="#050507"
                  level="M"
                  includeMargin={false}
                />
              </div>
              <p className="text-[11px] text-[#8B93A5] mt-4 font-mono tracking-wider uppercase break-all">
                {cardUrl}
              </p>
              <button
                onClick={copyLink}
                data-testid="card-copy-link"
                className="mt-4 text-xs uppercase tracking-wider border border-white/15 hover:border-white px-3 py-2 flex items-center gap-2"
              >
                <Copy className="w-3 h-3" /> Copy link
              </button>
            </div>

            <div className="space-y-3">
              <a
                href={vcfUrl}
                data-testid="card-download-vcf"
                className="btn-sweep block bg-[#39FF14] text-black font-bold uppercase tracking-wider px-6 py-4 text-sm hover:bg-[#32E612] transition-colors flex items-center justify-center gap-2"
              >
                <Download className="w-4 h-4" /> Save to Contacts (.vcf)
              </a>
              <button
                onClick={share}
                data-testid="card-share"
                className="block w-full border border-white/20 hover:border-white text-white px-6 py-4 text-sm uppercase tracking-wider flex items-center justify-center gap-2"
              >
                <Share2 className="w-4 h-4" /> Share Card
              </button>
              <Link
                to="/contact"
                data-testid="card-cta-meet"
                className="block w-full bg-transparent border border-[#00F0FF]/40 hover:border-[#00F0FF] text-[#00F0FF] px-6 py-4 text-sm uppercase tracking-wider flex items-center justify-center gap-2"
              >
                Book a meeting →
              </Link>
            </div>

            <div className="border border-dashed border-white/10 p-5 font-mono text-[10px] tracking-widest text-[#8B93A5] uppercase leading-relaxed">
              ▲ QUASECT · SOC LEVEL 2 / VAPT / 24×7
              <br />
              Built by operators with 40+ years of combined experience.
            </div>
          </div>
        </motion.div>
      </main>
    </div>
  );
}

function Action({ href, icon: Icon, label, value, external, disabled, testid }) {
  const inner = (
    <div className={`px-5 py-5 flex flex-col gap-1 ${disabled ? "opacity-40" : "hover:bg-white/[0.03]"}`}>
      <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.2em] uppercase text-[#00F0FF]">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="text-sm text-white truncate">{value}</div>
    </div>
  );
  if (disabled) return inner;
  if (external) {
    return (
      <a href={href} target="_blank" rel="noreferrer" data-testid={testid} className="transition-colors">
        {inner}
      </a>
    );
  }
  return (
    <a href={href} data-testid={testid} className="transition-colors">
      {inner}
    </a>
  );
}
